import React, { useState, useEffect } from "react";
import "./App.css";
import { addEffect } from "@react-three/fiber";
import Lenis from "@studio-freight/lenis";
import Scene from "./Scene";
import EmergingText from "./components/EmergingText";
import { sections } from "./sections";
import origin42Logo from "./assets/origin42Logo.png";

// Initialize smooth scrolling
const lenis = new Lenis();
addEffect((t) => lenis.raf(t));

function App() {
  const [animate, setAnimate] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalOpacity, setModalOpacity] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);

  // New states for entrance animation
  const [showOriginAnimation, setShowOriginAnimation] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [showOtherGridElements, setShowOtherGridElements] = useState(false);
  const [originAnimationComplete, setOriginAnimationComplete] = useState(false);

  // Track if we're returning from a modal (to skip animation)
  const [returningFromModal, setReturningFromModal] = useState(false);

  // Typewriter effect states
  const [showCursor, setShowCursor] = useState(true);
  const [cursorBlinks, setCursorBlinks] = useState(0);
  const [typedOrigin, setTypedOrigin] = useState("");
  const [typedBinary, setTypedBinary] = useState([]); // Initialize as array for vertical layout
  const [typedFortyTwo, setTypedFortyTwo] = useState([]); // Initialize as array
  const [currentPhase, setCurrentPhase] = useState("origin"); // 'origin', 'binary', 'fortytwo'
  const [startTyping, setStartTyping] = useState(false);
  const [hideCursor, setHideCursor] = useState(false); // New state to hide cursor after animation

  // Helper function to handle bold text formatting and HTML links
  const renderTextWithFormatting = (text) => {
    // First split by HTML links
    const linkRegex = /(<a[^>]*>.*?<\/a>)/g;
    const parts = text.split(linkRegex);

    return parts.map((part, partIndex) => {
      // Check if this part is an HTML link
      if (part.match(linkRegex)) {
        // Extract href and link text
        const hrefMatch = part.match(/href="([^"]*)"/);
        const textMatch = part.match(/>([^<]*)</);

        if (hrefMatch && textMatch) {
          const href = hrefMatch[1];
          const linkText = textMatch[1];

          return (
            <a
              key={partIndex}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "black",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {linkText}
            </a>
          );
        }
        return part;
      } else {
        // Handle bold text formatting for non-link parts
        return part.split(/(\*\*.*?\*\*)/).map((subPart, subIndex) => {
          if (subPart.startsWith("**") && subPart.endsWith("**")) {
            return (
              <strong key={`${partIndex}-${subIndex}`}>
                {subPart.slice(2, -2)}
              </strong>
            );
          }
          return subPart;
        });
      }
    });
  };

  // Enhanced modal content renderer with bullet point support
  const renderModalContent = (body) => {
    return body.split("\n\n").map((paragraph, index) => {
      // Check if this paragraph contains bullet points
      const lines = paragraph.split("\n");
      const hasBulletPoints = lines.some((line) =>
        line.trim().match(/^[•\-\*]\s+/)
      );

      if (hasBulletPoints) {
        // Render as a list
        const listItems = [];
        let currentParagraph = "";

        lines.forEach((line, lineIndex) => {
          const trimmedLine = line.trim();

          if (trimmedLine.match(/^[•\-\*]\s+/)) {
            // This is a bullet point
            if (currentParagraph) {
              // Add any preceding paragraph content
              listItems.push(
                <p key={`para-${index}-${lineIndex}`}>
                  {renderTextWithFormatting(currentParagraph)}
                </p>
              );
              currentParagraph = "";
            }

            // Remove bullet symbol and render the content
            const bulletContent = trimmedLine.replace(/^[•\-\*]\s+/, "");
            listItems.push(
              <li key={`bullet-${index}-${lineIndex}`}>
                {renderTextWithFormatting(bulletContent)}
              </li>
            );
          } else if (trimmedLine) {
            // Regular text line
            currentParagraph += (currentParagraph ? " " : "") + trimmedLine;
          }
        });

        // Add any remaining paragraph content
        if (currentParagraph) {
          listItems.push(
            <p key={`para-${index}-final`}>
              {renderTextWithFormatting(currentParagraph)}
            </p>
          );
        }

        // Wrap bullet points in a ul element
        const bullets = listItems.filter(
          (item) => item.key && item.key.includes("bullet")
        );
        const paragraphs = listItems.filter(
          (item) => item.key && item.key.includes("para")
        );

        return (
          <div key={index}>
            {paragraphs}
            {bullets.length > 0 && <ul key={`list-${index}`}>{bullets}</ul>}
          </div>
        );
      } else {
        // Regular paragraph without bullet points
        return <p key={index}>{renderTextWithFormatting(paragraph)}</p>;
      }
    });
  };

  // Canvas pointer events management for modal scrolling
  useEffect(() => {
    if (showModal) {
      // Disable pointer events on canvas when modal is open
      const canvas = document.querySelector("canvas");
      if (canvas) {
        canvas.style.pointerEvents = "none";
      }

      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    } else {
      // Re-enable pointer events when modal is closed
      const canvas = document.querySelector("canvas");
      if (canvas) {
        canvas.style.pointerEvents = "auto";
      }

      // Restore body scroll
      document.body.style.overflow = "unset";
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = "unset";
      const canvas = document.querySelector("canvas");
      if (canvas) {
        canvas.style.pointerEvents = "auto";
      }
    };
  }, [showModal]);

  // Initialize entrance animation on component mount
  useEffect(() => {
    let cursorInterval;

    // Start cursor blinking immediately
    cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
      setCursorBlinks((prev) => {
        const newBlinkCount = prev + 1;

        // Stop blinking after exactly 4 state changes (2 complete blinks: on->off->on->off)
        if (newBlinkCount >= 4) {
          clearInterval(cursorInterval);

          // Reveal everything immediately after the blink finishes, no typing animation
          setCurrentPhase("binary");
          setTypedOrigin("origin");
          setTypedBinary(["|", "O", "|", "O", "|", "O"]);
          setTypedFortyTwo(["FORTY", "TWO"]);
          setHideCursor(true);
          setOriginAnimationComplete(true);
          setShowOtherGridElements(true);
        }

        return newBlinkCount;
      });
    }, 600); // Blink every 600ms

    return () => {
      if (cursorInterval) {
        clearInterval(cursorInterval);
      }
    };
  }, []);

  const handleSectionClick = (section) => {
    setSelectedSection(sections[section]);

    // Reset animation state and modal
    setAnimate(false);
    setShowModal(false);
    setModalOpacity(0);
    setAnimationComplete(false); // Reset animation completion state

    // Start animation after a brief delay
    setTimeout(() => {
      setAnimate(true);

      // Show modal immediately with opacity 0 (instead of after animation completes)
      setShowModal(true);

      // Start increasing the modal opacity gradually during the animation
      const opacitySteps = 20; // Number of opacity steps
      const animationDuration = 1500; // Match with animation duration in EmergingText (1.5s)
      const stepDuration = animationDuration / opacitySteps;

      // Create stepped opacity animation
      let step = 0;
      const opacityInterval = setInterval(() => {
        step++;
        // Calculate opacity - starts slow, accelerates towards the end (ease-in-out effect)
        // Using cubicInOut-like function
        const progress = step / opacitySteps;
        const eased =
          progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;

        setModalOpacity(eased);

        if (step >= opacitySteps) {
          clearInterval(opacityInterval);
          setModalOpacity(1); // Ensure we reach exactly 1 at the end

          // Set animation complete flag after a small delay to allow final renders
          setTimeout(() => {
            setAnimationComplete(true);
            console.log("Animation complete and modal fully rendered!");
            // Here you can trigger any actions that should happen when the animation is fully complete
          }, 50);
        }
      }, stepDuration);
    }, 100);
  };

  // Close modal and return to grid view - UPDATED to remove animation
  const handleCloseModal = (e) => {
    e.stopPropagation(); // Prevent event bubbling

    // Mark that we're returning from modal to skip animation
    setReturningFromModal(true);

    // Immediately reset all states - no delays or animations
    setShowModal(false);
    setModalOpacity(0);
    setAnimationComplete(false);
    setAnimate(false);
    setSelectedSection(null); // Reset immediately instead of with setTimeout
  };

  // Show origin animation screen
  if (!originAnimationComplete) {
    return (
      <>
        {/* Pass modal state and animation completion to Scene component */}
        <Scene
          showModal={showModal}
          modalOpacity={modalOpacity}
          animationComplete={animationComplete}
        />

        <div className={`centered-grid-container grid-enter`}>
          <div className="grid-layout animation-mode">
            {/* Left column (50%) */}
            <div className="left-column">
              <div className="grid-cell business-development hidden-grid-element">
                <h2>Business Development</h2>
              </div>
              <div className="grid-cell venture hidden-grid-element">
                <h2>Venture</h2>
              </div>
            </div>

            {/* Right column (50%) */}
            <div className="right-column">
              <div className="grid-cell innovation hidden-grid-element">
                <h2>Innovation</h2>
              </div>

              <div className="right-bottom-section">
                <div className="left-side-container">
                  <div className="top-row">
                    <div className="grid-cell binary hidden-grid-element">
                      <h2>101010</h2>
                    </div>
                    <div className="grid-cell origin">
                      <div className="origin-container">
                        {!originAnimationComplete ? (
                          !hideCursor && (
                            <div
                              className={`cursor-thin ${
                                !startTyping && showCursor
                                  ? "visible"
                                  : startTyping
                                  ? "static"
                                  : "hidden"
                              }`}
                            ></div>
                          )
                        ) : (
                          <img
                            src={origin42Logo}
                            alt="origin 42"
                            className="origin-logo"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="grid-cell consultancy hidden-grid-element">
                    <h2>Advisory</h2>
                  </div>
                </div>
                <div className="about-us hidden-grid-element">
                  <div className="about-us-main">
                    <h2>About Us</h2>
                  </div>
                  <div className="impressum-box">
                    <span className="impressum-text">impressum</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Pass modal state and animation completion to Scene component */}
      <Scene
        showModal={showModal}
        modalOpacity={modalOpacity}
        animationComplete={animationComplete}
      />

      {!selectedSection ? (
        <div
          className={`centered-grid-container ${
            returningFromModal ? "no-animation" : "grid-enter"
          }`}
        >
          <div
            className={`grid-layout ${
              !showOtherGridElements ? "animation-mode" : ""
            }`}
          >
            {/* Left column (50%) */}
            <div className="left-column">
              <div
                className={`grid-cell business-development ${
                  !showOtherGridElements
                    ? "hidden-grid-element"
                    : "visible-grid-element"
                }`}
                onClick={() => handleSectionClick("Business Development")}
              >
                <h2>Business Development</h2>
              </div>
              <div
                className={`grid-cell venture ${
                  !showOtherGridElements
                    ? "hidden-grid-element"
                    : "visible-grid-element"
                }`}
                onClick={() => handleSectionClick("Venture")}
              >
                <h2>Venture</h2>
              </div>
            </div>

            {/* Right column (50%) */}
            <div className="right-column">
              <div
                className={`grid-cell innovation ${
                  !showOtherGridElements
                    ? "hidden-grid-element"
                    : "visible-grid-element"
                }`}
                onClick={() => handleSectionClick("Innovation")}
              >
                <h2>Innovation</h2>
              </div>

              <div className="right-bottom-section">
                <div className="left-side-container">
                  <div className="top-row">
                    <div
                      className={`grid-cell binary ${
                        !showOtherGridElements
                          ? "hidden-grid-element"
                          : "visible-grid-element"
                      }`}
                      onClick={() => handleSectionClick("Binary")}
                    >
                      <h2>101010</h2>
                    </div>
                    <div
                      className="grid-cell origin"
                      // REMOVED: onClick={() => handleSectionClick("Origin")}
                    >
                      <div className="origin-container">
                        <img
                          src={origin42Logo}
                          alt="origin 42"
                          className="origin-logo"
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className={`grid-cell consultancy ${
                      !showOtherGridElements
                        ? "hidden-grid-element"
                        : "visible-grid-element"
                    }`}
                    onClick={() => handleSectionClick("Consultancy")}
                  >
                    <h2>Advisory</h2>
                  </div>
                </div>
                <div
                  className={`about-us ${
                    !showOtherGridElements
                      ? "hidden-grid-element"
                      : "visible-grid-element"
                  }`}
                >
                  <div
                    className="about-us-main"
                    onClick={() => handleSectionClick("About Us")}
                  >
                    <h2>About Us</h2>
                  </div>
                  <div
                    className="impressum-box"
                    onClick={() => handleSectionClick("Impressum")}
                  >
                    <span className="impressum-text">impressum</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Content view with EmergingText when a section is selected
        <div
          className="content-view"
          onClick={showModal ? handleCloseModal : ""}
        >
          <div className="grid__item">
            <div className="grid__item-img">
              <EmergingText
                type={0}
                animate={animate}
                title=""
                description=""
              />

              {/* Modal that appears with opacity transition during animation */}
              {showModal && (
                <div
                  className="modal-overlay"
                  style={{
                    opacity: modalOpacity,
                    transition: "opacity 0.05s ease-out", // Smooth transitions between steps
                  }}
                  onClick={handleCloseModal} // Click overlay to close
                >
                  <div
                    className="modal-content"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent closing when clicking content
                    }}
                    onWheel={(e) => {
                      // Ensure wheel events are not prevented
                      e.stopPropagation();
                    }}
                    style={{
                      opacity: modalOpacity,
                      className: animationComplete ? "animation-completed" : "",
                    }}
                  >
                    {/* Updated modal header structure */}
                    <div className="modal-header">
                      <h2>{selectedSection.title}</h2>
                      <button
                        className="modal-close"
                        onClick={handleCloseModal}
                      >
                        ×
                      </button>
                    </div>

                    <div
                      className="modal-body"
                      onWheel={(e) => {
                        // Additional wheel event handling for the body
                        e.stopPropagation();
                      }}
                    >
                      {renderModalContent(selectedSection.body)}
                    </div>

                    {/* Content that appears only after animation completes */}
                    {animationComplete && (
                      <div className="post-animation-content">
                        {/* Additional content here */}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default App;
